import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import caretRightFilter from '../../../../../assets/icons/ic_caret_right_filter.svg';
import caretLeft from '../../../../../assets/icons/ic_caret_left.svg';
import icStatus from '../../../../../assets/icons/ic_status.svg';
import icRemoveFilter from '../../../../../assets/icons/ic_remove_filter.svg';

const emptyFilters = {
	verification_status: [],
	is_subscription: [],
	is_matched: []
};

const ProviderApplicationsFilterDialog = ({ initialFilters, onApply, onClose }) => {
	const dictionary = useDictionary();

	const [showDialog, setShowDialog] = useState(false);
	const [activePanel, setActivePanel] = useState(null);
	const [draft, setDraft] = useState({
		verification_status: initialFilters?.verification_status ?? [],
		is_subscription: initialFilters?.is_subscription ?? [],
		is_matched: initialFilters?.is_matched ?? []
	});

	// Filter group definitions: each option is { value, text }.
	const groups = [
		{
			key: 'verification_status',
			label: dictionary.status,
			options: [
				{ value: '1', text: dictionary.unverified },
				{ value: '2', text: dictionary.pending },
				{ value: '3', text: dictionary.verified },
				{ value: '4', text: dictionary.rejected }
			]
		},
		{
			key: 'is_subscription',
			label: dictionary.plan,
			options: [
				{ value: '1', text: dictionary.business_plan },
				{ value: '0', text: dictionary.basic }
			]
		},
		{
			key: 'is_matched',
			label: dictionary.place_matched,
			options: [
				{ value: '1', text: dictionary.matched },
				{ value: '0', text: dictionary.not_matched }
			]
		}
	];

	useEffect(() => {
		setTimeout(() => setShowDialog(true), 50);
	}, []);

	const dismissDialog = () => {
		setShowDialog(false);
		setTimeout(() => onClose(), 600);
	};

	const optionText = (groupKey, value) => {
		const group = groups.find((g) => g.key === groupKey);
		const option = group?.options.find((o) => o.value === value);
		return option ? option.text : value;
	};

	const toggleOption = (groupKey, value) => {
		setDraft((prev) => {
			const current = prev[groupKey] ?? [];
			const next = current.includes(value)
				? current.filter((v) => v !== value)
				: [...current, value];
			return { ...prev, [groupKey]: next };
		});
	};

	const removeOption = (groupKey, value) => {
		setDraft((prev) => ({
			...prev,
			[groupKey]: (prev[groupKey] ?? []).filter((v) => v !== value)
		}));
	};

	const handleReset = () => {
		setDraft({ ...emptyFilters });
		onApply({ ...emptyFilters });
		dismissDialog();
	};

	const handleApply = () => {
		onApply(draft);
		dismissDialog();
	};

	const renderGroupRow = (group) => {
		const selected = draft[group.key] ?? [];
		return (
			<React.Fragment key={group.key}>
				<div className="group-filter">
					<div className="group-filter-icon-label">
						<img className="group-filter-icon" src={icStatus} alt="" />
						<span
							onClick={() => setActivePanel(group.key)}
							className="group-filter-label"
						>
							{group.label}
						</span>
					</div>
					<img
						onClick={() => setActivePanel(group.key)}
						className="icon-to-select"
						src={caretRightFilter}
						alt=""
					/>
				</div>
				<div className="group-filter-selected-status">
					{selected.map((value) => (
						<div className="filter-selected-item" key={value}>
							<span>{optionText(group.key, value)}</span>
							<img
								onClick={() => removeOption(group.key, value)}
								src={icRemoveFilter}
								alt=""
							/>
						</div>
					))}
				</div>
			</React.Fragment>
		);
	};

	const renderPanel = (group) => (
		<div className="group-filter-hidden inputs">
			<a
				href="#"
				onClick={(e) => {
					e.preventDefault();
					setActivePanel(null);
				}}
				className="back-to-filter-list"
			>
				<img src={caretLeft} alt="" />
				<span>{dictionary.back}</span>
			</a>
			{group.options.map((option) => {
				const checked = (draft[group.key] ?? []).includes(option.value);
				return (
					<div
						key={option.value}
						onClick={() => toggleOption(group.key, option.value)}
						className="form-check-container"
						style={{ marginLeft: '0' }}
						data-testid="custom-checkbox"
					>
						<div
							style={{ width: '16px', height: '16px' }}
							className={
								'custom-checkbox ' +
								(checked ? 'custom-checkbox-checked' : '')
							}
						/>
						<label
							style={{ marginBottom: 0, marginLeft: '10px', fontWeight: 400 }}
							className="checkbox-label"
						>
							{option.text}
						</label>
					</div>
				);
			})}
		</div>
	);

	const activeGroup = groups.find((g) => g.key === activePanel);

	return (
		<div
			onClick={dismissDialog}
			className="dialog-wrapper"
			style={{ background: 'transparent', position: 'fixed', zIndex: '11' }}
			data-testid="provider-applications-filter-dialog-wrapper"
		>
			<div
				onClick={(e) => e.stopPropagation()}
				className={
					showDialog
						? 'experience-category-tags-wrapper experience-category-tags-wrapper-visible'
						: 'experience-category-tags-wrapper'
				}
				data-testid="provider-applications-filter-dialog"
			>
				<div className="schedule-dialog-content-wrapper thin-scrollbar">
					<div
						className="dialog-close-button"
						style={{ right: '24px', top: '21px' }}
						onClick={dismissDialog}
						data-testid="provider-applications-filter-dialog-close-button"
					/>

					<div className="schedule-dialog-content-container schedule-dialog-content-container-pa-0">
						<div className="schedule-dialog-content-container-content border-bottom">
							<h2
								style={{
									whiteSpace: 'pre-line',
									fontSize: '16px',
									lineHeight: '24px',
									fontWeight: 500
								}}
							>
								{dictionary.filters}
							</h2>
						</div>

						<div className="schedule-dialog-content-container-content">
							{activeGroup ? (
								renderPanel(activeGroup)
							) : (
								<div className="box-filter-list">
									{groups.map((group) => renderGroupRow(group))}
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="filter-dialog-button-container">
					<button
						onClick={handleReset}
						className="schedule-dialog-reset-button"
						data-testid="provider-applications-filter-reset-button"
					>
						{dictionary.reset}
					</button>
					<button
						onClick={handleApply}
						className="button filter-dialog-apply-button"
						data-testid="provider-applications-filter-apply-button"
					>
						{dictionary.apply}
					</button>
				</div>
			</div>
		</div>
	);
};

ProviderApplicationsFilterDialog.propTypes = {
	initialFilters: PropTypes.object,
	onApply: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired
};

export default ProviderApplicationsFilterDialog;
